<template>
  <div class="h-full w-full">
    <canvas ref="canvasEl" class="h-full w-full"></canvas>
  </div>
</template>

<script>
import { Chart, registerables } from "chart.js";

Chart.register(...registerables);

export default {
  props: {
    labels: { type: Array, required: true },
    values: { type: Array, required: true },
    values2: { type: Array, required: false },
    color: { type: String, default: "#12b8c9" }
    ,
    color2: { type: String, default: "#2563eb" }
  },

  data() {
    return {
      chart: null
    };
  },

  mounted() {
    this.create();
  },

  beforeDestroy() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  },

  watch: {
    labels() {
      this.renderOrUpdate();
    },
    values() {
      this.renderOrUpdate();
    },
    color() {
      this.renderOrUpdate();
    }
  },

  methods: {
    validateColor(color) {
      if (!color || typeof color !== "string") {
        return "#12b8c9";
      }
      const normalized = color.replace("#", "");
      const isValid = /^[0-9A-Fa-f]{3}$|^[0-9A-Fa-f]{6}$/.test(normalized);
      return isValid ? color : "#12b8c9";
    },

    hexToRgba(hexColor, alpha = 1) {
      const validColor = this.validateColor(hexColor);
      
      const normalized = validColor.replace("#", "");
      const expanded =
        normalized.length === 3
          ? normalized
              .split("")
              .map((char) => char + char)
              .join("")
          : normalized;

      const red = parseInt(expanded.slice(0, 2), 16);
      const green = parseInt(expanded.slice(2, 4), 16);
      const blue = parseInt(expanded.slice(4, 6), 16);

      return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
    },

    create() {
      if (!this.$refs.canvasEl) return;

      const validColor = this.validateColor(this.color);

      this.chart = new Chart(this.$refs.canvasEl, {
        type: "line",

        data: {
          labels: this.labels,
          datasets: (function() {
            const ds = [
              {
                data: this.values,
                borderColor: validColor,
                backgroundColor: this.hexToRgba(validColor, 0.22),
                fill: true,
                tension: 0.35,
                borderWidth: 3,
                pointRadius: 3,
                pointHoverRadius: 4,
                pointBackgroundColor: "#ffffff",
                pointBorderColor: validColor,
                pointBorderWidth: 2
              }
            ];

            if (Array.isArray(this.values2)) {
              const validColor2 = this.validateColor(this.color2);
              ds.push({
                data: this.values2,
                borderColor: validColor2,
                backgroundColor: this.hexToRgba(validColor2, 0.18),
                fill: true,
                tension: 0.35,
                borderWidth: 3,
                pointRadius: 3,
                pointHoverRadius: 4,
                pointBackgroundColor: "#ffffff",
                pointBorderColor: validColor2,
                pointBorderWidth: 2
              });
            }

            return ds;
          }).call(this)
        },

        options: {
          responsive: true,
          maintainAspectRatio: false,

          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              backgroundColor: "#ffffff",
              titleColor: "#1e293b",
              bodyColor: "#475569",
              borderColor: "#e5e7eb",
              borderWidth: 1,
              padding: 10,
              displayColors: false
            }
          },

          scales: {
            x: {
              grid: {
                display: false,
                drawBorder: false
              },
              ticks: {
                color: "#6b7280",
                font: {
                  size: 11,
                  weight: "600"
                }
              }
            },

            y: {
              beginAtZero: true,
              grid: {
                color: "#e5e7eb",
                drawBorder: false
              },
              ticks: {
                color: "#6b7280",
                font: {
                  size: 11
                },
                stepSize: 10
              }
            }
          }
        }
      });
    },

    renderOrUpdate() {
      if (this.chart) {
        const validColor = this.validateColor(this.color);
        this.chart.data.labels = this.labels;
        if (this.chart.data.datasets[0]) {
          this.chart.data.datasets[0].data = this.values;
          this.chart.data.datasets[0].borderColor = validColor;
          this.chart.data.datasets[0].backgroundColor = this.hexToRgba(validColor, 0.22);
          this.chart.data.datasets[0].pointBorderColor = validColor;
        }

        if (Array.isArray(this.values2)) {
          const validColor2 = this.validateColor(this.color2);
          if (this.chart.data.datasets[1]) {
            this.chart.data.datasets[1].data = this.values2;
            this.chart.data.datasets[1].borderColor = validColor2;
            this.chart.data.datasets[1].backgroundColor = this.hexToRgba(validColor2, 0.18);
            this.chart.data.datasets[1].pointBorderColor = validColor2;
          } else {
            this.chart.data.datasets.push({
              data: this.values2,
              borderColor: validColor2,
              backgroundColor: this.hexToRgba(validColor2, 0.18),
              fill: true,
              tension: 0.35,
              borderWidth: 3,
              pointRadius: 3,
              pointHoverRadius: 4,
              pointBackgroundColor: "#ffffff",
              pointBorderColor: validColor2,
              pointBorderWidth: 2
            });
          }
        } else if (this.chart.data.datasets[1]) {
          this.chart.data.datasets.splice(1, 1);
        }

        this.chart.update();
        return;
      }

      this.create();
    }
  }
};
</script>

<style scoped></style>